import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiClient } from '@/lib/apiClient'
import { accessToken, fetchMe } from '@/lib/staffSession'

import type {
  ConductorContest,
  ConductorItem,
  ConductorRound,
  ConductorVote,
  FlowResult,
  PlanActivity,
  PlanEvent,
  PerformanceChoice,
  PlanStepFields,
  ProgramSegment,
  PublicCakeMoment,
  PublicProgramItem,
  QuizQuestionFields,
  ScreenState,
  SegmentFields,
  SegmentKind,
} from './types'

/**
 * Ce que le compte connecté a le droit de faire, sur cette édition.
 *
 * Le conducteur vit hors du back-office (pas de `useAdminContext`) : il lit
 * les mêmes droits, sous la même clé de cache. Ils ne servent qu'à MASQUER un
 * bouton qui serait refusé — chaque appel reste vérifié par le serveur.
 */
export function useStaffPermissions(eventId: string | undefined, enabled: boolean) {
  const me = useQuery({
    queryKey: ['admin-me', eventId],
    queryFn: () => fetchMe(eventId),
    enabled: Boolean(eventId) && enabled,
    retry: false,
  })
  return (permission: string) => Boolean(me.data?.permissions.includes(permission))
}

/** Programme public — RULE 10 : lisible sans billet ni compte. */
export function usePublicProgram(slug: string | undefined) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['public-program', slug, i18n.language],
    queryFn: () =>
      apiClient.get<PublicProgramItem[]>(`/public/events/${slug}/program`, {
        locale: i18n.language,
      }),
    enabled: Boolean(slug),
    // Le soir J, la page publique doit suivre la scène comme l'écran de salle.
    refetchInterval: 60_000,
  })
}

export function usePublicCakeMoment(slug: string | undefined) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['public-cake', slug, i18n.language],
    queryFn: () =>
      apiClient.get<PublicCakeMoment>(`/public/events/${slug}/cake-moment`, {
        locale: i18n.language,
      }),
    enabled: Boolean(slug),
    // Une édition sans Cake Moment configuré répond 404 : c'est une absence,
    // pas une panne — inutile de réessayer.
    retry: false,
  })
}

/**
 * Écran de la salle.
 *
 * La cadence de rafraîchissement vient du serveur (`screen.refresh_seconds`,
 * réglable par édition) : accélérer l'écran ne demande pas de redéploiement.
 */
export function useScreenState(key: string | null, slug: string | null) {
  const { i18n } = useTranslation()

  // La langue est dans la clé et dans `Accept-Language` : sans elle, la télé
  // affichait les intitulés du programme dans la langue par défaut du
  // serveur, quelle que soit la langue choisie (ou imposée par `?lang=`).
  return useQuery({
    queryKey: ['screen-program', key, slug, i18n.language],
    queryFn: () => {
      const params = new URLSearchParams({ key: key ?? '' })
      if (slug) params.set('slug', slug)
      return apiClient.get<ScreenState>(`/screen/program?${params.toString()}`, {
        locale: i18n.language,
      })
    },
    // Changer de langue ne doit pas faire clignoter la télé sur « En attente » :
    // l'ancien état reste affiché le temps que le nouveau arrive.
    placeholderData: (previous) => previous,
    enabled: Boolean(key),
    // Pendant un tour du concours ou un vote, la télé relit toutes les cinq
    // secondes au plus : le vote que le MC ouvre « pour vingt secondes », son
    // « +30 s », ou le vote qu'il ferme, doivent s'y voir sans attendre le
    // cycle ordinaire. Entre deux lectures, le décompte tourne seul.
    refetchInterval: (query) => {
      const data = query.state.data
      const seconds = data?.refresh_seconds ?? 15
      // Un tour qui tourne compte aussi : le candidat que le MC fait défiler
      // doit apparaître en moins de cinq secondes, pas au cycle suivant. Un
      // calque posé aussi : quand le MC le retire, la télé doit suivre vite.
      // Et toute étape en cours : c'est pendant une étape que le MC publie un
      // pronostic ou un gagnant, et ce nom ne doit pas attendre quinze
      // secondes. Une seule télé qui relit toutes les cinq secondes ne pèse rien.
      const counting =
        Boolean(data?.now) ||
        (data?.open_votes.length ?? 0) > 0 ||
        data?.now?.kind === 'competition_round' ||
        Boolean(data?.round_running) ||
        Boolean(data?.on_stage) ||
        Boolean(data?.overlay)
      return (counting ? Math.min(seconds, 5) : seconds) * 1000
    },
    retry: false,
  })
}

export function useConductor(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['conductor', eventId],
    queryFn: async () =>
      apiClient.get<ConductorItem[]>('/ops/program', {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      }),
    enabled: Boolean(eventId) && enabled,
    refetchInterval: 20_000,
  })
}

export type FlowAction = 'start' | 'end' | 'skip' | 'reset'

export function useFlowAction(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      itemId,
      action,
      note,
    }: {
      itemId: string
      action: FlowAction
      note?: string
    }) =>
      apiClient.post<FlowResult>(
        `/ops/program/${itemId}/${action}`,
        { note: note ?? null },
        { token: await accessToken(), ...(eventId ? { eventId } : {}) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['conductor', eventId] })
      // Lancer une étape du déroulé démarre les tours qui lui sont rattachés.
      void queryClient.invalidateQueries({ queryKey: ['conductor-contest', eventId] })
    },
  })
}

// ── Concours : tours et scrutins, au conducteur ────────────────────

interface Paged<T> {
  items: T[]
}

/**
 * Tout ce que le MC pilote du concours, en un rafraîchissement.
 *
 * Trois lectures qui existaient déjà côté équipe — les tours, les scrutins,
 * les concours — rassemblées ici parce que le MC n'a qu'une main libre : il
 * ne va pas chercher le vote d'un tour dans un autre écran.
 */
export function useContestBoard(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['conductor-contest', eventId],
    queryFn: async () => {
      const options = { token: await accessToken(), ...(eventId ? { eventId } : {}) }
      const [rounds, votes, contests] = await Promise.all([
        apiClient.get<ConductorRound[]>('/ops/competition/rounds', options),
        apiClient.get<Paged<ConductorVote>>(
          `/admin/events/${eventId}/vote-categories?page_size=200`,
          options,
        ),
        apiClient.get<Paged<ConductorContest>>(
          `/admin/events/${eventId}/competition/categories?page_size=200`,
          options,
        ),
      ])
      return { rounds, votes: votes.items, contests: contests.items }
    },
    enabled: Boolean(eventId) && enabled,
    // Le compteur de bulletins doit bouger pendant que la salle vote.
    refetchInterval: 10_000,
    retry: false,
  })
}

export type RoundAction = 'start' | 'end'
export type VoteAction = 'open' | 'freeze' | 'close' | 'publish' | 'unpublish'

export function useContestAction(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      target: { kind: 'round'; id: string; action: RoundAction } | {
        kind: 'vote'
        id: string
        action: VoteAction
        /** Obligatoire pour `unpublish` : le motif part au journal. */
        reason?: string
        /** À l'ouverture : la durée du décompte annoncé à la salle, en secondes. */
        durationSeconds?: number | null
      },
    ) => {
      const options = { token: await accessToken(), ...(eventId ? { eventId } : {}) }
      const path =
        target.kind === 'round'
          ? `/ops/competition/rounds/${target.id}/${target.action}`
          : `/admin/events/${eventId}/vote-categories/${target.id}/${target.action}`
      const body =
        target.kind !== 'vote'
          ? {}
          : target.reason
            ? { reason: target.reason }
            : target.action === 'open' && target.durationSeconds
              ? { duration_seconds: target.durationSeconds }
              : {}
      return apiClient.post<unknown>(path, body, options)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['conductor-contest', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['conductor', eventId] })
      // L'aperçu de l'écran de la salle suit le vote : ouvert, décompte, gelé.
      void queryClient.invalidateQueries({ queryKey: ['conductor-screen', eventId] })
    },
  })
}

/**
 * Qui défile maintenant — le geste du MC pendant le tour.
 *
 * `null` : personne ; l'écran de la salle revient au programme. Le cockpit
 * et l'aperçu de l'écran se relisent aussitôt : le MC voit ce que la salle voit.
 */
export function useOnStage(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (target: { roundId: string; candidateId: string | null }) =>
      apiClient.post<ConductorRound>(
        `/ops/competition/rounds/${target.roundId}/on-stage`,
        { candidate_id: target.candidateId },
        { token: await accessToken(), ...(eventId ? { eventId } : {}) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['conductor-contest', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['conductor-screen', eventId] })
    },
  })
}

// ── Préparer le déroulé ────────────────────────────────────────────

/**
 * L'édition et ses animations, telles que l'éditeur du déroulé les lit.
 *
 * La page publique de l'édition ne dit pas sa langue par défaut — celle où le
 * libellé est obligatoire. On lit donc la fiche d'équipe (`event.event.read`,
 * que le MC détient), et les animations pour les proposer au choix.
 */
export function usePlanContext(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['conductor-plan', eventId],
    queryFn: async () => {
      const options = { token: await accessToken(), ...(eventId ? { eventId } : {}) }
      const [event, activities] = await Promise.all([
        apiClient.get<PlanEvent>(`/admin/events/${eventId}`, options),
        apiClient.get<Paged<PlanActivity>>(
          `/admin/events/${eventId}/activities?page_size=200`,
          options,
        ),
      ])
      return { event, activities: activities.items }
    },
    enabled: Boolean(eventId) && enabled,
    retry: false,
  })
}

export type PlanChange =
  | { kind: 'create'; fields: PlanStepFields; afterId: string | null }
  | { kind: 'update'; id: string; fields: Partial<PlanStepFields> }
  | { kind: 'delete'; id: string }
  | { kind: 'move'; id: string; direction: 'up' | 'down' }
  | { kind: 'shift'; id: string; minutes: number }

/**
 * Tous les gestes de préparation passent par une seule mutation : le déroulé
 * est relu après chacun, parce qu'un ajout, un décalage ou un échange touche
 * d'autres lignes que celle qu'on vient de toucher.
 */
export function usePlanChange(eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (change: PlanChange) => {
      const options = { token: await accessToken(), ...(eventId ? { eventId } : {}) }
      switch (change.kind) {
        case 'create':
          return apiClient.post<unknown>(
            '/ops/program/items',
            { ...change.fields, after_id: change.afterId },
            options,
          )
        case 'update':
          return apiClient.patch<unknown>(`/ops/program/items/${change.id}`, change.fields, options)
        case 'delete':
          return apiClient.delete<unknown>(`/ops/program/items/${change.id}`, options)
        case 'move':
          return apiClient.post<unknown>(
            `/ops/program/items/${change.id}/move`,
            { direction: change.direction },
            options,
          )
        case 'shift':
          return apiClient.post<unknown>(
            `/ops/program/items/${change.id}/shift`,
            { minutes: change.minutes },
            options,
          )
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['conductor', eventId] })
      // Même navigateur, même table : la page publique ouverte dans un autre
      // onglet se relit tout de suite plutôt qu'à son prochain cycle.
      void queryClient.invalidateQueries({ queryKey: ['public-program'] })
    },
  })
}

// ── Le cockpit du MC : l'écran de la salle ─────────────────────────

/**
 * Ce que l'écran de la salle affiche en ce moment, lu derrière la session
 * d'équipe (`/ops/program/screen`) — sans la clé d'affichage, qui reste à qui
 * règle l'édition. Même cadence que l'écran lui-même.
 */
export function useScreenPreview(eventId: string | undefined, enabled: boolean) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['conductor-screen', eventId, i18n.language],
    queryFn: async () =>
      apiClient.get<ScreenState>('/ops/program/screen', {
        token: await accessToken(),
        locale: i18n.language,
        ...(eventId ? { eventId } : {}),
      }),
    enabled: Boolean(eventId) && enabled,
    refetchInterval: (query) => (query.state.data?.refresh_seconds ?? 15) * 1000,
    retry: false,
  })
}

// ── La feuille du MC : sous-parties et affichage à l'écran ─────────

/**
 * Les sous-parties de toutes les étapes, en une lecture.
 *
 * Privées au MC (`program.flow.control` ou `program.item.update`) : la page
 * publique et l'écran de la salle ne les lisent jamais.
 */
export function useSegments(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['conductor-segments', eventId],
    queryFn: async () =>
      apiClient.get<ProgramSegment[]>('/ops/program/segments', {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      }),
    enabled: Boolean(eventId) && enabled,
    // Une autre tablette (le responsable d'événement) peut noter un gagnant.
    refetchInterval: 30_000,
    retry: false,
  })
}

/** Les concours de l'édition — pour rattacher des prestations ou un pronostic. */
export function useContests(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['conductor-contests', eventId],
    queryFn: async () =>
      (
        await apiClient.get<Paged<ConductorContest>>(
          `/admin/events/${eventId}/competition/categories?page_size=200`,
          { token: await accessToken(), ...(eventId ? { eventId } : {}) },
        )
      ).items,
    enabled: Boolean(eventId) && enabled,
    staleTime: 60_000,
    retry: false,
  })
}

export type SegmentChange =
  | { kind: 'create'; itemId: string; segmentKind: SegmentKind; fields: SegmentFields }
  | { kind: 'update'; id: string; fields: Partial<SegmentFields> }
  | { kind: 'delete'; id: string }
  | { kind: 'move'; id: string; direction: 'up' | 'down' }
  | {
      kind: 'performance'
      candidateId: string
      choice: PerformanceChoice | null
      topic: string | null
      notes: string | null
    }
  | { kind: 'show'; id: string; show: 'candidates' | 'winner'; categoryIds?: string[] }
  | { kind: 'clear' }
  // Le quiz : ses questions, et chacune posée (puis révélée) à l'écran.
  | { kind: 'questionCreate'; segmentId: string; fields: QuizQuestionFields }
  | { kind: 'questionUpdate'; id: string; fields: Partial<QuizQuestionFields> }
  | { kind: 'questionDelete'; id: string }
  | { kind: 'questionMove'; id: string; direction: 'up' | 'down' }
  | { kind: 'questionShow'; id: string }
  | { kind: 'questionReveal'; id: string }

/**
 * Tous les gestes sur la feuille du MC, et l'écran relu après chacun : ce
 * que la salle voit doit se lire dans le cockpit sans attendre son cycle.
 */
export function useSegmentChange(eventId: string | undefined) {
  const queryClient = useQueryClient()
  const { i18n } = useTranslation()

  return useMutation({
    mutationFn: async (change: SegmentChange) => {
      const options = {
        token: await accessToken(),
        locale: i18n.language,
        ...(eventId ? { eventId } : {}),
      }
      switch (change.kind) {
        case 'create':
          return apiClient.post<unknown>(
            `/ops/program/items/${change.itemId}/segments`,
            { kind: change.segmentKind, ...change.fields },
            options,
          )
        case 'update':
          return apiClient.patch<unknown>(`/ops/program/segments/${change.id}`, change.fields, options)
        case 'delete':
          return apiClient.delete<unknown>(`/ops/program/segments/${change.id}`, options)
        case 'move':
          return apiClient.post<unknown>(
            `/ops/program/segments/${change.id}/move`,
            { direction: change.direction },
            options,
          )
        case 'performance':
          return apiClient.put<unknown>(
            `/ops/program/candidates/${change.candidateId}/performance`,
            { choice: change.choice, topic: change.topic, notes: change.notes },
            options,
          )
        case 'show':
          return apiClient.post<unknown>(
            `/ops/program/segments/${change.id}/screen`,
            { show: change.show, category_ids: change.categoryIds ?? [] },
            options,
          )
        case 'clear':
          return apiClient.delete<unknown>('/ops/program/screen/overlay', options)
        case 'questionCreate':
          return apiClient.post<unknown>(
            `/ops/program/segments/${change.segmentId}/questions`,
            change.fields,
            options,
          )
        case 'questionUpdate':
          return apiClient.patch<unknown>(
            `/ops/program/quiz-questions/${change.id}`,
            change.fields,
            options,
          )
        case 'questionDelete':
          return apiClient.delete<unknown>(`/ops/program/quiz-questions/${change.id}`, options)
        case 'questionMove':
          return apiClient.post<unknown>(
            `/ops/program/quiz-questions/${change.id}/move`,
            { direction: change.direction },
            options,
          )
        case 'questionShow':
          return apiClient.post<unknown>(`/ops/program/quiz-questions/${change.id}/screen`, {}, options)
        case 'questionReveal':
          return apiClient.post<unknown>(`/ops/program/quiz-questions/${change.id}/reveal`, {}, options)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['conductor-segments', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['conductor-screen', eventId] })
    },
  })
}
